// APIクライアント。Pages Functions の /api 配下を叩く。
import type {
  Site,
  UrlEntry,
  CheckRecord,
  CheckJobResponse,
  CheckJobStatusResponse,
  LeadListItem,
} from '@shared/types';

const BASE = '/api';

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch {
      // ignore parse error
    }
    throw new Error(msg);
  }
  // 204 等ボディなし
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ── サイト ─────────────────────────────────────────
export function listSites(): Promise<Site[]> {
  return req<Site[]>('/sites');
}

export function createSite(input: {
  name: string;
  clientName: string | null;
}): Promise<Site> {
  return req<Site>('/sites', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateSite(
  siteId: string,
  input: { name: string; clientName: string | null },
): Promise<Site> {
  return req<Site>(`/sites/${encodeURIComponent(siteId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteSite(siteId: string): Promise<{ deleted: boolean }> {
  return req(`/sites/${encodeURIComponent(siteId)}`, { method: 'DELETE' });
}

// ── URL ────────────────────────────────────────────
export function listUrls(siteId: string): Promise<UrlEntry[]> {
  return req<UrlEntry[]>(`/sites/${encodeURIComponent(siteId)}/urls`);
}

export function createUrl(input: {
  siteId: string;
  url: string;
  label: string | null;
}): Promise<UrlEntry> {
  return req<UrlEntry>('/urls', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateUrl(
  urlId: string,
  input: { url: string; label: string | null },
): Promise<UrlEntry> {
  return req<UrlEntry>(`/urls/${encodeURIComponent(urlId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function deleteUrl(urlId: string): Promise<{ deleted: boolean }> {
  return req(`/urls/${encodeURIComponent(urlId)}`, { method: 'DELETE' });
}

// ── チェック ───────────────────────────────────────
export function startCheck(input: {
  url: string;
  urlId?: string;
}): Promise<CheckJobResponse> {
  return req<CheckJobResponse>('/checks', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function getCheck(checkId: string): Promise<CheckJobStatusResponse> {
  return req<CheckJobStatusResponse>(`/checks/${encodeURIComponent(checkId)}`);
}

export function listUrlChecks(urlId: string): Promise<CheckRecord[]> {
  return req<CheckRecord[]>(`/urls/${encodeURIComponent(urlId)}/checks`);
}

// ── 管理: リード ───────────────────────────────────
export function listLeads(): Promise<LeadListItem[]> {
  return req<LeadListItem[]>('/admin/leads');
}

/**
 * チェックジョブをポーリングして完了まで待つ。
 * running の間 intervalMs おきに再取得。done/error で解決。
 */
export async function pollCheck(
  checkId: string,
  opts: {
    intervalMs?: number;
    timeoutMs?: number;
    onTick?: (s: CheckJobStatusResponse) => void;
  } = {},
): Promise<CheckJobStatusResponse> {
  const intervalMs = opts.intervalMs ?? 2500;
  const timeoutMs = opts.timeoutMs ?? 120_000;
  const start = Date.now();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const s = await getCheck(checkId);
    opts.onTick?.(s);
    if (s.status !== 'running') return s;
    if (Date.now() - start > timeoutMs) {
      throw new Error('チェックがタイムアウトしました。時間をおいて履歴を確認してください。');
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}
